import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, AlertTriangle, Clock, Search, ExternalLink, Building2, DollarSign, Calendar, Package } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import MciPackageDrawer from '@/components/procurement/MciPackageDrawer';
import InlineEdit from '@/components/procurement/InlineEdit';

const RISK_COLORS = { HIGH: '#EF4444', MEDIUM: '#F59E0B', LOW: '#10B981' };
const CAT_BG = { BULK: 'var(--tone-blue-bg)', TAGGED: 'var(--tone-amber-bg)' };
const CAT_FG = { BULK: 'var(--tone-blue-text)', TAGGED: 'var(--tone-amber-text)' };

/** Multi-signal risk: combines gate slippage, RAS buffer AND forecast LT slippage.
 *  HIGH   → gate late > 14d OR RAS buffer < 15d OR forecastLT > planLT by >30d/>25%
 *  MEDIUM → gate late > 7d  OR RAS buffer < 45d OR forecastLT > planLT by >15d/>15%
 *  LOW    → everything else
 *
 *  Forecast LT signal is critical: a 90d planned LT that has stretched to
 *  142d (+52d) means the whole pipeline has slipped 8 weeks — never LOW
 *  regardless of RAS buffer. Reads `forecastLeadTimeVarianceDays` written
 *  by the backend enrichment layer in procurement_schedule_routes.py.
 */
function computeRisk(entry) {
  const now = new Date();
  const allGates = [...(entry.preOrderProgress || []), ...(entry.postOrderProgress || [])];
  const nextGate = allGates.find(g => !g.actualDate && g.plannedDate);
  const gateLate = nextGate ? Math.floor((now - new Date(nextGate.plannedDate)) / 86400000) : null;
  const ras = entry.requiredAtSiteDate ? new Date(entry.requiredAtSiteDate) : null;
  const rasBuffer = ras ? Math.floor((ras - now) / 86400000) : null;

  // Forecast lead-time slippage (already computed backend-side).
  // Prefer the pre-computed variance; fall back to a fresh calculation if
  // the entry only carries forecast/planned totals.
  const fltVar = entry.forecastLeadTimeVarianceDays != null
    ? entry.forecastLeadTimeVarianceDays
    : (entry.forecastLeadTimeDays != null && entry.plannedLeadTimeDays
        ? entry.forecastLeadTimeDays - entry.plannedLeadTimeDays
        : null);
  const planLt = entry.plannedLeadTimeDays || 0;
  const fltPct = fltVar != null && planLt ? (fltVar / planLt) * 100 : null;

  const isHigh   = (gateLate != null && gateLate > 14)
                || (rasBuffer != null && rasBuffer < 15)
                || (fltVar != null && (fltVar > 30 || (fltPct != null && fltPct > 25)));
  if (isHigh) return 'HIGH';
  const isMedium = (gateLate != null && gateLate > 7)
                || (rasBuffer != null && rasBuffer < 45)
                || (fltVar != null && (fltVar > 15 || (fltPct != null && fltPct > 15)));
  if (isMedium) return 'MEDIUM';
  return 'LOW';
}

function daysToNextGate(entry) {
  const gates = [...(entry.preOrderProgress || []), ...(entry.postOrderProgress || [])];
  const nx = gates.find(g => !g.actualDate && g.plannedDate);
  if (!nx) return null;
  return Math.floor((new Date(nx.plannedDate) - new Date()) / 86400000);
}

function fmtCurrency(n) {
  if (n == null || isNaN(n)) return '—';
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}k`;
  return `$${Number(n).toLocaleString()}`;
}

function fmtDate(d) {
  if (!d) return '—';
  return String(d).slice(0, 10);
}

function SwimlaneGantt({ entries, onRowClick }) {
  const { isDark } = useTheme();
  if (!entries?.length) return null;

  // Build calendar-based lanes: for each MCI, find MR (earliest) and Site Receipt (latest) dates,
  // then plot Planned vs Actual bars along a shared timeline.
  const rows = entries.slice(0, 40).map(e => {
    const all = [...(e.preOrderProgress || []), ...(e.postOrderProgress || [])];
    const first = all[0], last = all[all.length - 1];
    const plannedStart = first?.plannedDate ? new Date(first.plannedDate) : null;
    const plannedEnd   = last?.plannedDate  ? new Date(last.plannedDate)  : null;
    const actualStart  = all.find(g => g.actualDate)?.actualDate ? new Date(all.find(g => g.actualDate).actualDate) : null;
    const actualDone   = [...all].reverse().find(g => g.actualDate);
    const actualEnd    = actualDone ? new Date(actualDone.actualDate) : new Date();
    return {
      mci: e.mciLineNumber,
      label: `${e.mciLineNumber} · ${(e.materialDescription || '').slice(0, 24)}`,
      vendor: e.vendor,
      risk: e.risk,
      plannedStart, plannedEnd, actualStart, actualEnd,
      progressPercent: e.overallProgress || 0,
    };
  }).filter(r => r.plannedStart && r.plannedEnd);

  if (!rows.length) return <div className="p-6 text-center text-xs" style={{ color: 'var(--text-muted)' }}>No dates to plot</div>;

  // Shared timeline
  const minDate = new Date(Math.min(...rows.map(r => r.plannedStart.getTime())));
  const maxDate = new Date(Math.max(...rows.map(r => r.plannedEnd.getTime())));
  const span   = Math.max(1, maxDate - minDate);
  const today  = new Date();
  const pct = (d) => `${((d - minDate) / span) * 100}%`;
  // Bars are clamped to the track: `actualEnd` falls back to today, which can sit
  // past the last planned date and would otherwise overflow the lane.
  const bar = (start, end) => {
    const l = Math.min(100, Math.max(0, ((start - minDate) / span) * 100));
    const w = Math.min(100 - l, Math.max(0.5, ((end - start) / span) * 100));
    return { left: `${l}%`, width: `${w}%` };
  };

  // Month gridlines — only those that actually fall inside the plotted span,
  // so the first label cannot sit at a negative offset and get clipped.
  const months = [];
  const cursor = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
  while (cursor < maxDate) {
    if (cursor >= minDate) months.push(new Date(cursor));
    cursor.setMonth(cursor.getMonth() + 1);
  }

  return (
    <div className="p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
          Lead Time Timeline · click a bar to open drilldown
        </h4>
        <div className="flex items-center gap-3 text-[10px]" style={{ color: 'var(--text-muted)' }}>
          <span className="flex items-center gap-1"><span className="inline-block w-4 h-2 rounded" style={{ background: '#3B82F6', opacity: 0.35 }} /> Planned MR → Site</span>
          <span className="flex items-center gap-1"><span className="inline-block w-4 h-2 rounded" style={{ background: '#22C55E' }} /> Actual (risk-tinted)</span>
          <span className="flex items-center gap-1"><span className="inline-block w-0.5 h-3" style={{ background: '#EF4444' }} /> Today</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div style={{ minWidth: 800 }}>
          {/* Month axis — mirrors a row exactly (200px label gutter + the track),
              so labels, gridlines and bars all measure against the same box. */}
          <div className="flex items-end text-[9px] pb-1" style={{ color: 'var(--text-muted)' }}>
            <div className="w-[200px] flex-shrink-0" />
            <div className="relative flex-1 h-4">
              {months.map((m, i) => (
                <div key={i} className="absolute bottom-0 font-mono whitespace-nowrap"
                  style={{ left: pct(m), transform: 'translateX(2px)' }}>
                  {m.toLocaleDateString(undefined, { month: 'short', year: '2-digit' })}
                </div>
              ))}
            </div>
          </div>

          {rows.map((r, i) => (
            <div key={r.mci} className="flex items-center py-1 cursor-pointer hover:bg-[var(--hover-bg)]"
              onClick={() => onRowClick?.(r.mci)}
              style={{ borderTop: i > 0 ? '1px solid var(--border-color)' : 'none' }}
              data-testid={`gantt-${r.mci}`}>
              <div className="w-[200px] pr-2 flex-shrink-0">
                <div className="font-mono text-[10px] font-semibold truncate" style={{ color: 'var(--tone-navy-text)' }} title={r.label}>{r.mci}</div>
                <div className="text-[9px] truncate" style={{ color: 'var(--text-muted)' }} title={r.vendor}>{r.vendor || '—'}</div>
              </div>
              <div className="relative flex-1 h-6 rounded" style={{ background: isDark ? '#1e293b20' : 'var(--tone-slate-bg)' }}>
                {/* Month gridlines */}
                {months.map((m, mi) => (
                  <div key={mi} className="absolute top-0 bottom-0 w-px"
                    style={{ left: pct(m), background: 'var(--border-color)', opacity: 0.6 }} />
                ))}
                {/* Planned bar */}
                <div className="absolute top-1 h-4 rounded"
                  style={{
                    ...bar(r.plannedStart, r.plannedEnd),
                    background: '#3B82F6', opacity: 0.35,
                  }} />
                {/* Actual bar */}
                {r.actualStart && (
                  <div className="absolute top-2 h-2 rounded"
                    style={{
                      ...bar(r.actualStart, r.actualEnd),
                      background: RISK_COLORS[r.risk],
                    }} />
                )}
                {/* Today marker */}
                {today >= minDate && today <= maxDate && (
                  <div className="absolute top-0 bottom-0 w-0.5"
                    style={{ left: pct(today), background: '#EF4444' }} />
                )}
                {/* Progress badge */}
                <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[9px] font-mono font-bold"
                  style={{ color: 'var(--text-primary)' }}>{r.progressPercent}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function MCILongLeadTracker({ projectId }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table');
  const [riskFilter, setRiskFilter] = useState('ALL');
  const [catFilter, setCatFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [drillMci, setDrillMci] = useState(null);

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/v1/projects/${projectId}/mci/entries?is_long_lead=true&limit=1000`);
      setEntries(data.entries || []);
    } catch (e) {
      // Skip toast on aborts / project-switch races — the second call will succeed.
      // Only surface real server errors to the user.
      const status = e?.response?.status;
      if (status && status >= 500) toast.error('Failed to load long-lead items');
      else if (!e?.response && e?.code !== 'ERR_CANCELED') {
        // eslint-disable-next-line no-console
        console.warn('Long-Lead fetch failed, will retry on next projectId change:', e?.message);
      }
    }
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    fetchData();
    const h = () => fetchData();
    window.addEventListener('mci-register-updated', h);
    return () => window.removeEventListener('mci-register-updated', h);
  }, [fetchData]);

  const enriched = useMemo(() => entries.map(e => ({
    ...e, risk: computeRisk(e), daysToNext: daysToNextGate(e),
  })), [entries]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return enriched.filter(e =>
      (riskFilter === 'ALL' || e.risk === riskFilter) &&
      (catFilter === 'ALL' || (e.materialCategory || '').toUpperCase() === catFilter) &&
      (!s || (e.mciLineNumber || '').toLowerCase().includes(s) ||
              (e.materialDescription || '').toLowerCase().includes(s) ||
              (e.vendor || '').toLowerCase().includes(s) ||
              (e.tagNumber || e.bulkItemCode || '').toLowerCase().includes(s))
    );
  }, [enriched, riskFilter, catFilter, search]);

  const kpis = useMemo(() => ({
    total:  enriched.length,
    high:   enriched.filter(e => e.risk === 'HIGH').length,
    medium: enriched.filter(e => e.risk === 'MEDIUM').length,
    low:    enriched.filter(e => e.risk === 'LOW').length,
    avgLT:  enriched.length ? Math.round(enriched.reduce((s, e) => s + (e.plannedLeadTimeDays || 0), 0) / enriched.length) : 0,
    exposure: enriched.reduce((s, e) => s + (e.poValue || 0), 0),
  }), [enriched]);

  if (loading) return <div className="flex items-center justify-center py-20" data-testid="long-lead-loading">
    <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--tone-navy-text)' }} /></div>;

  const btnActiveSty = { background: '#1E4E8C', color: '#ffffff' };
  const btnIdleSty   = { background: 'var(--card-bg)', color: 'var(--text-muted)' };

  return (
    <div className="space-y-4" data-testid="long-lead-tracker">
      {/* KPIs (6) */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          { label: 'Total Long Lead', v: kpis.total,  c: 'var(--tone-navy-text)', icon: Package },
          { label: 'High Risk',       v: kpis.high,   c: '#EF4444', icon: AlertTriangle },
          { label: 'Medium Risk',     v: kpis.medium, c: '#F59E0B', icon: Clock },
          { label: 'On Track',        v: kpis.low,    c: '#10B981', icon: Clock },
          { label: 'Avg Lead Time',   v: `${kpis.avgLT}d`, c: 'var(--tone-navy-text)', icon: Calendar },
          { label: 'PO Exposure',     v: fmtCurrency(kpis.exposure), c: '#F26B1F', icon: DollarSign },
        ].map((k, i) => {
          const Icon = k.icon;
          return (
            <div key={i} className="p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}
              data-testid={`ll-kpi-${i}`}>
              <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold" style={{ color: 'var(--text-muted)' }}>
                <Icon className="w-3 h-3" style={{ color: k.c }} />{k.label}
              </div>
              <div className="text-xl font-bold mt-1" style={{ color: k.c }}>{k.v}</div>
            </div>
          );
        })}
      </div>

      {/* Filters + Search */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="MCI · description · vendor · tag" data-testid="ll-search"
            className="pl-8 pr-3 py-1.5 rounded-lg text-xs border w-64"
            style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
        </div>

        <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: 'var(--border-color)' }}>
          {['table', 'gantt'].map(m => (
            <button key={m} onClick={() => setViewMode(m)} className="px-3 py-1.5 text-xs font-semibold capitalize transition-colors"
              style={viewMode === m ? btnActiveSty : btnIdleSty}
              data-testid={`ll-view-${m}`}>{m}</button>
          ))}
        </div>

        <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: 'var(--border-color)' }}>
          {['ALL', 'BULK', 'TAGGED'].map(c => (
            <button key={c} onClick={() => setCatFilter(c)} className="px-3 py-1.5 text-xs font-semibold transition-colors"
              style={catFilter === c ? btnActiveSty : btnIdleSty}
              data-testid={`ll-cat-${c.toLowerCase()}`}>{c}</button>
          ))}
        </div>

        <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: 'var(--border-color)' }}>
          {['ALL', 'HIGH', 'MEDIUM', 'LOW'].map(r => (
            <button key={r} onClick={() => setRiskFilter(r)} className="px-3 py-1.5 text-xs font-semibold transition-colors"
              style={riskFilter === r
                ? { background: RISK_COLORS[r] || '#1E4E8C', color: '#ffffff' }
                : btnIdleSty}
              data-testid={`ll-risk-${r.toLowerCase()}`}>{r}</button>
          ))}
        </div>

        <div className="ml-auto text-[11px]" style={{ color: 'var(--text-muted)' }}>
          Showing {filtered.length} of {enriched.length}
        </div>
      </div>

      {viewMode === 'gantt' ? (
        <SwimlaneGantt entries={filtered} onRowClick={setDrillMci} />
      ) : (
        <div className="overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg)' }}>
          <table className="w-full text-[11px]" data-testid="long-lead-table">
            <thead>
              <tr style={{ background: 'var(--card-bg-alt)' }}>
                <th className="px-2 py-2 text-left font-bold uppercase text-[10px]" style={{ color: 'var(--text-muted)' }}>MCI#</th>
                <th className="px-2 py-2 text-left font-bold uppercase text-[10px]" style={{ color: 'var(--text-muted)' }}>Category</th>
                <th className="px-2 py-2 text-left font-bold uppercase text-[10px]" style={{ color: 'var(--text-muted)' }}>Tag/Code</th>
                <th className="px-2 py-2 text-left font-bold uppercase text-[10px]" style={{ color: 'var(--text-muted)' }}>Description</th>
                <th className="px-2 py-2 text-left font-bold uppercase text-[10px]" style={{ color: 'var(--text-muted)' }}>Vendor</th>
                <th className="px-2 py-2 text-left font-bold uppercase text-[10px]" style={{ color: 'var(--text-muted)' }}>Priority</th>
                <th className="px-2 py-2 text-left font-bold uppercase text-[10px]" style={{ color: 'var(--text-muted)' }}>PO #</th>
                <th className="px-2 py-2 text-right font-bold uppercase text-[10px]" style={{ color: 'var(--text-muted)' }}>PO Value</th>
                <th className="px-2 py-2 text-center font-bold uppercase text-[10px]" style={{ color: 'var(--text-muted)' }}>RAS</th>
                <th className="px-2 py-2 text-center font-bold uppercase text-[10px]" style={{ color: 'var(--text-muted)' }}>Planned LT</th>
                <th className="px-2 py-2 text-center font-bold uppercase text-[10px]" style={{ color: 'var(--text-muted)' }} title="MR (actual or planned) → last-site forecast">Forecast LT</th>
                <th className="px-2 py-2 text-center font-bold uppercase text-[10px]" style={{ color: 'var(--text-muted)' }}>Actual LT</th>
                <th className="px-2 py-2 text-center font-bold uppercase text-[10px]" style={{ color: 'var(--text-muted)' }}>Variance</th>
                <th className="px-2 py-2 text-center font-bold uppercase text-[10px]" style={{ color: 'var(--text-muted)' }}>Next Gate ETA</th>
                <th className="px-2 py-2 text-center font-bold uppercase text-[10px]" style={{ color: 'var(--text-muted)' }}>Progress</th>
                <th className="px-2 py-2 text-center font-bold uppercase text-[10px]" style={{ color: 'var(--text-muted)' }}>Risk</th>
                <th className="px-2 py-2 text-center font-bold uppercase text-[10px]" style={{ color: 'var(--text-muted)' }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(e => {
                const cat = (e.materialCategory || '').toUpperCase();
                const variance = e.leadTimeVarianceDays;
                return (
                  <tr key={e.id} className="border-t hover:bg-[var(--hover-bg)] cursor-pointer transition-colors"
                    style={{ borderColor: 'var(--border-color)' }}
                    onClick={() => setDrillMci(e.mciLineNumber)}
                    data-testid={`ll-row-${e.mciLineNumber}`}>
                    <td className="px-2 py-1.5 font-mono font-bold" style={{ color: 'var(--tone-navy-text)' }}>{e.mciLineNumber}</td>
                    <td className="px-2 py-1.5">
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold"
                        style={{ background: CAT_BG[cat] || 'var(--card-bg-alt)', color: CAT_FG[cat] || 'var(--text-muted)' }}>
                        {cat || '—'}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 font-mono" style={{ color: 'var(--text-primary)' }}>{e.tagNumber || e.bulkItemCode || '—'}</td>
                    <td className="px-2 py-1.5 max-w-[240px] truncate" style={{ color: 'var(--text-primary)' }} title={e.materialDescription}>{e.materialDescription}</td>
                    <td className="px-2 py-1.5" onClick={ev => ev.stopPropagation()}>
                      <InlineEdit value={e.vendor} projectId={projectId} mci={e.mciLineNumber}
                        field="vendor" onSaved={fetchData} testIdPrefix="ll-vendor" />
                    </td>
                    <td className="px-2 py-1.5 text-center font-bold text-[10px]" onClick={ev => ev.stopPropagation()}>
                      <InlineEdit value={e.priority} projectId={projectId} mci={e.mciLineNumber}
                        field="priority" type="select" options={['CRITICAL','HIGH','NORMAL','LOW']}
                        onSaved={fetchData} testIdPrefix="ll-priority"
                        style={{ color: e.priority === 'CRITICAL' ? '#F26B1F' : e.priority === 'HIGH' ? '#EF4444' : 'var(--text-muted)' }} />
                    </td>
                    <td className="px-2 py-1.5 font-mono" onClick={ev => ev.stopPropagation()}>
                      <InlineEdit value={e.poNumber} projectId={projectId} mci={e.mciLineNumber}
                        field="poNumber" onSaved={fetchData} testIdPrefix="ll-po" />
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmtCurrency(e.poValue)}</td>
                    <td className="px-2 py-1.5 text-center font-mono text-[10px]" style={{ color: 'var(--text-muted)' }}>{fmtDate(e.requiredAtSiteDate)}</td>
                    <td className="px-2 py-1.5 text-center font-mono" style={{ color: 'var(--text-muted)' }}>{e.plannedLeadTimeDays || '—'}d</td>
                    <td className="px-2 py-1.5 text-center font-mono text-[10px]"
                      style={{ color: e.forecastLeadTimeDays != null ? '#F59E0B' : 'var(--text-disabled)' }}
                      data-testid={`ll-flt-${e.mciLineNumber}`}
                      title={e.forecastLeadTimeVarianceDays != null ? `Variance vs plan: ${e.forecastLeadTimeVarianceDays > 0 ? '+' : ''}${e.forecastLeadTimeVarianceDays}d` : ''}>
                      {e.forecastLeadTimeDays != null ? `${e.forecastLeadTimeDays}d` : '—'}
                    </td>
                    <td className="px-2 py-1.5 text-center font-mono" style={{ color: e.actualLeadTimeDays ? 'var(--text-primary)' : 'var(--text-disabled)' }}>{e.actualLeadTimeDays ? `${e.actualLeadTimeDays}d` : '—'}</td>
                    <td className="px-2 py-1.5 text-center font-mono"
                      style={{ color: variance > 0 ? '#EF4444' : variance < 0 ? '#10B981' : 'var(--text-muted)' }}>
                      {variance != null ? `${variance > 0 ? '+' : ''}${variance}d` : '—'}
                    </td>
                    <td className="px-2 py-1.5 text-center font-mono"
                      style={{ color: e.daysToNext != null && e.daysToNext < 0 ? '#EF4444' : e.daysToNext != null && e.daysToNext < 7 ? '#F59E0B' : 'var(--text-muted)' }}>
                      {e.daysToNext != null ? `${e.daysToNext}d` : '—'}
                    </td>
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-1">
                        <div className="flex-1 h-1.5 rounded-full min-w-[40px]" style={{ background: 'var(--card-bg-alt)' }}>
                          <div className="h-full rounded-full" style={{
                            width: `${e.overallProgress || 0}%`,
                            background: (e.overallProgress || 0) >= 80 ? '#10B981' : (e.overallProgress || 0) >= 40 ? '#F59E0B' : '#EF4444'
                          }} />
                        </div>
                        <span className="text-[9px] font-mono w-8 text-right" style={{ color: 'var(--text-muted)' }}>{(e.overallProgress || 0).toFixed(0)}%</span>
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold"
                        style={{ background: `${RISK_COLORS[e.risk]}20`, color: RISK_COLORS[e.risk] }}>
                        {e.risk}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <ExternalLink className="w-3.5 h-3.5 inline" style={{ color: 'var(--tone-navy-text)' }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="py-12 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
              {entries.length === 0
                ? 'No long-lead items found. Items with lead time > 16 weeks are flagged automatically.'
                : 'No items match the current filters.'}
            </div>
          )}
        </div>
      )}

      {drillMci && (
        <MciPackageDrawer projectId={projectId} mci={drillMci}
          onClose={() => { setDrillMci(null); fetchData(); }} />
      )}
    </div>
  );
}
